import React, { useEffect, useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField,
} from '@mui/material';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { AuthService } from '../../services/auth.service';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import { useToast } from '../../context/ToastContext';
import CommercialPartyAutocomplete from '../commercial/CommercialPartyAutocomplete';

interface Props {
    open: boolean;
    order: IBuyerOrder;
    onClose: () => void;
    onUpdated: (updated: IBuyerOrder) => void;
}

/**
 * PATCH `{id}/amend` — the only writable surface once an order has left DRAFT (H4/REV 2,
 * garments-merchandising T5/T10). Touches `shipDate`, `remarks` and the agent override/commission.
 * The server treats a null agent as "clear", so the current agent and commission are always sent
 * back pre-filled; an emptied remarks box is sent as "" so it clears. The caller only renders
 * this dialog for CONFIRMED/IN_PRODUCTION orders (`AMENDABLE_BUYER_ORDER_STATUSES`) — hidden for
 * DRAFT (still fully editable via the header) and for SHIPPED/CLOSED/CANCELLED.
 */
export function BuyerOrderAmendDialog({ open, order, onClose, onUpdated }: Props) {
    const toast = useToast();
    const [shipDate, setShipDate] = useState('');
    const [remarks, setRemarks] = useState('');
    const [agentPartyId, setAgentPartyId] = useState<string | null>(null);
    const [commission, setCommission] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setShipDate(order.shipDate);
            setRemarks(order.remarks ?? '');
            setAgentPartyId(order.agentPartyId ?? null);
            setCommission(order.agentCommissionPercent != null ? String(order.agentCommissionPercent) : '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, order.id]);

    const pct = Number(commission);
    const commissionError = commission.trim() && (Number.isNaN(pct) || pct < 0 || pct > 100)
        ? 'Commission must be between 0 and 100' : null;

    const handleSubmit = async () => {
        if (!shipDate) {
            toast.showError('Ship date is required');
            return;
        }
        if (commissionError) {
            toast.showError(commissionError);
            return;
        }
        const institutionId = AuthService.getSelectedInstitution();
        setSaving(true);
        try {
            const updated = await BuyerOrderService.amend(institutionId, order.id, {
                shipDate,
                remarks: remarks.trim(),
                agentPartyId,
                agentCommissionPercent: commission.trim() ? Number(commission) : null,
            });
            toast.showSuccess('Order amended');
            onUpdated(updated);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to amend order');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'merchandising.orderAmend.page' } as any}>
            <DialogTitle>Amend Order</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }} data-help-section="merchandising.orderAmend.section.amend">
                    <Alert severity="info">
                        Only the ship date, remarks, agent and commission can be amended once an order is confirmed.
                        Quantities and lines cannot be amended in v1 — cancel and re-key the order
                        for a scope change.
                    </Alert>
                    <TextField
                        data-help="merchandising.orderAmend.field.shipDate"
                        label="Ship Date" type="date" size="small" fullWidth required disabled={saving}
                        InputLabelProps={{ shrink: true }}
                        value={shipDate} onChange={e => setShipDate(e.target.value)}
                    />
                    <TextField
                        data-help="merchandising.orderAmend.field.remarks"
                        label="Remarks" size="small" fullWidth multiline rows={2} disabled={saving}
                        value={remarks} onChange={e => setRemarks(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                    />
                    <CommercialPartyAutocomplete
                        data-help="merchandising.orderAmend.field.agent"
                        institutionId={AuthService.getSelectedInstitution()}
                        partyTypes={['BUYING_AGENT']}
                        label="Buying agent (overrides buyer)"
                        value={agentPartyId}
                        onChange={party => setAgentPartyId(party?.id ?? null)}
                        disabled={saving}
                        fullWidth
                    />
                    <TextField
                        data-help="merchandising.orderAmend.field.agentCommission"
                        label="Agent commission %" type="number" size="small" fullWidth disabled={saving}
                        value={commission} onChange={e => setCommission(e.target.value)}
                        error={!!commissionError} helperText={commissionError ?? undefined}
                        inputProps={{ step: 0.01, min: 0, max: 100 }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="merchandising.orderAmend.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="merchandising.orderAmend.submit" variant="contained" onClick={handleSubmit} disabled={saving}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BuyerOrderAmendDialog;
